import React from "react";
import { BsFilePdf } from "react-icons/bs";
import { FaFile } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { MdTimeline } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { queries } from "lib/build/dashboards/ktx/queries";
import { Status } from "lib/build/forms/ktx/committeeOpinion";

import { Tooltip } from "src/common/components/Tooltip";
import { useFormatName } from "src/common/hooks/useFormatName";
import {
	Permission,
	useAuthState,
	useHasPermission,
	useKnownUsers
} from "src/features/auth";
import {
	useLastEventByQuery,
	useLastEventValueByQuery
} from "src/features/events";
import {
	PatientLink,
	usePatientIdParam,
	usePatientName,
	useSelectPatient
} from "src/features/patient";
import { node as patientStatementOfConsentFyrms } from "src/modules/ktx/forms/basic-registration-data/consents/patientStatementOfConsentFyrms";
import { node as patientStatementOfConsentOvsz } from "src/modules/ktx/forms/basic-registration-data/consents/patientStatementOfConsentOvsz";
import { AbdominalPelvisUltrasound } from "src/modules/ktx/header/components/AbdominalPelvisUltrasound";
import { DialysisCentre } from "src/modules/ktx/header/components/DialysisCentre";
import { KidneyDiseaseEthiology } from "src/modules/ktx/header/components/KidneyDiseaseEthiology";
import { ShortStatus } from "src/modules/ktx/header/components/ShortStatus";
import { TransplantAnesthesiology } from "src/modules/ktx/header/components/TransplantAnesthesiology";
import { TransplantNephrology } from "src/modules/ktx/header/components/TransplantNephrology";
import { TransplantSurgery } from "src/modules/ktx/header/components/TransplantSurgery";
import { TranstorachalisEcho } from "src/modules/ktx/header/components/TranstorachalisEcho";
import { useAge } from "src/modules/ktx/header/hooks/useAge";
import { useBloodGroup } from "src/modules/ktx/header/hooks/useBloodGroup";
import { useBmi } from "src/modules/ktx/header/hooks/useBmi";
import { useDm } from "src/modules/ktx/header/hooks/useDm";
import { useHeight } from "src/modules/ktx/header/hooks/useHeight";
import { useHt } from "src/modules/ktx/header/hooks/useHt";
import { useLiveDonorScreening } from "src/modules/ktx/header/hooks/useLiveDonorScreening";
import { useLongStatus } from "src/modules/ktx/header/hooks/useLongStatus";
import { useNephrologistOpinion } from "src/modules/ktx/header/hooks/useNephrologistOpinion";
import { usePatientEtId } from "src/modules/ktx/header/hooks/usePatientEtId";
import { usePDFData } from "src/modules/ktx/header/hooks/usePDFData";
import { usePreviousOtherOrganTransplantation } from "src/modules/ktx/header/hooks/usePreviousOtherOrganTransplantation";
import { useRenalReplacementTherapy } from "src/modules/ktx/header/hooks/useRenalReplacementTherapy";
import { useSocialSecurityNumber } from "src/modules/ktx/header/hooks/useSocialSecurityNumber";
import { useWaitingListTime } from "src/modules/ktx/header/hooks/useWaitingListTime";
import { useWeight } from "src/modules/ktx/header/hooks/useWeight";
import { messages } from "src/modules/ktx/header/messages";
import { generatePDF } from "src/modules/ktx/header/utils/generatePDF";
import { waitingListMessages } from "src/modules/ktx/messages";
import {
	Header as BareHeader,
	HeaderAlert,
	HeaderStatus,
	HeaderValue,
	renderTitle
} from "src/modules/shared/header";

import "./Header.css";

export const Header: React.FunctionComponent = () => {
	const canEdit = useCanEdit();
	const canViewTimeline = useCanViewTimeline();
	const status =
		useLastEventValueByQuery<Status>(queries.patientStatus) || Status.NEW;
	const longStatus = useLongStatus();
	return (
		<BareHeader
			module="ktx"
			variant={status}
			status={
				<PatientLink to="/status-history">
					<HeaderStatus title={longStatus} progress={0}>
						<ShortStatus />
					</HeaderStatus>
				</PatientLink>
			}
			title={
				<PatientLink to="/dashboard">
					<Name />
				</PatientLink>
			}
			actions={
				<>
					<StatementOfConsentAlert />
					{canEdit && <EditLink />}
					{canViewTimeline && <TimelineLink />}
					<ReportLink />
					<PDFButton />
				</>
			}
			values={
				<>
					<SocialSecurityNumber />
					<BloodGroup />
					<Age />
					<Weight />
					<Height />
					<Bmi />
					<PatientEtId />
					<WaitingListTime />
					<RenalReplacementTherapy />
					<Dm />
					<Ht />
					<NephrologistOpinion />
					<KidneyDiseaseEthiology />
					<TransplantAnesthesiology />
					<TransplantSurgery />
					<TransplantNephrology />
					<AbdominalPelvisUltrasound />
					<TranstorachalisEcho />
					<DialysisCentre />
					<LiveDonorScreening />
					<PreviousOtherOrganTransplantation />
				</>
			}
		/>
	);
};

function useCanEdit(): boolean {
	const patientId = usePatientIdParam();

	const { user } = useAuthState();
	const { patient } = useSelectPatient(patientId);

	const hasPatientsAllRWPerm = useHasPermission(Permission.PatientsAllRW);

	const isConnectedUser =
		user !== null &&
		patient !== null &&
		patient.connectedUsers &&
		patient.connectedUsers.includes(user.id);

	return hasPatientsAllRWPerm || isConnectedUser;
}

function useCanViewTimeline(): boolean {
	return useHasPermission(Permission.Timeline);
}

const EditLink: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<PatientLink to="/edit" title={formatMessage(messages.editPatient)}>
			<FiEdit />
		</PatientLink>
	);
};

const TimelineLink: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<PatientLink to="/timeline" title={formatMessage(messages.timeline)}>
			<MdTimeline />
		</PatientLink>
	);
};

const ReportLink: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<PatientLink to="/report" title={formatMessage(messages.report)}>
			<FaFile />
		</PatientLink>
	);
};

const PDFButton: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	const data = usePDFData();
	const onClick = React.useCallback(() => {
		generatePDF(data);
	}, [data]);
	return (
		<button
			type="button"
			title={formatMessage(messages.pdf)}
			onClick={onClick}
		>
			<BsFilePdf />
		</button>
	);
};

const StatementOfConsentAlert: React.FunctionComponent = () => {
	const fyrmsEvent = useLastEventByQuery<boolean>(
		queries.signedStatementOfConsentFyrms
	);

	const ovszEvent = useLastEventByQuery<boolean>(
		queries.signedStatementOfConsentOvsz
	);

	const { formatMessage } = useIntl();

	const fyrmsOk =
		fyrmsEvent?.value &&
		fyrmsEvent.attachments !== null &&
		fyrmsEvent.attachments.length !== 0;

	const ovszOk =
		ovszEvent?.value &&
		ovszEvent.attachments !== null &&
		ovszEvent.attachments.length !== 0;

	if (fyrmsOk && ovszOk) {
		return null;
	}

	let to: string;
	if (!fyrmsOk && !ovszOk) {
		to = "ktx-consents";
	} else {
		to = !fyrmsOk
			? patientStatementOfConsentFyrms.id
			: patientStatementOfConsentOvsz.id;
	}

	return (
		<PatientLink
			to={`/form/${to}`}
			title={formatMessage(messages.statementOfConsentAlert)}
		>
			<HeaderAlert />
		</PatientLink>
	);
};

const Name: React.FunctionComponent = () => {
	const value = usePatientName();
	return <>{value}</>;
};

const SocialSecurityNumber: React.FunctionComponent = () => {
	const value = useSocialSecurityNumber();
	return <HeaderValue variant="social-security-number">{value}</HeaderValue>;
};

const BloodGroup: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useBloodGroup();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = <>{value}</>;

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="blood-group">{content}</HeaderValue>}
		/>
	);
};

const Age: React.FunctionComponent = () => {
	const value = useAge();
	return <HeaderValue variant="age">{value}</HeaderValue>;
};

const Weight: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useWeight();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = <>{value}</>;

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="weight">{content}</HeaderValue>}
		/>
	);
};

const Height: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useHeight();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = <>{value}</>;

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="height">{content}</HeaderValue>}
		/>
	);
};

const Bmi: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useBmi();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>
			<FormattedMessage {...messages.bmi} />
			{": "}
			{value}
		</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="bmi">{content}</HeaderValue>}
		/>
	);
};

const PatientEtId: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = usePatientEtId();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>
			<FormattedMessage {...messages.patientEtId} />
			{": "}
			{value}
		</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={
				<HeaderValue variant="patient-et-id">{content}</HeaderValue>
			}
		/>
	);
};

const RenalReplacementTherapy: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useRenalReplacementTherapy();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>
			<FormattedMessage {...messages.renalReplacementTherapy} />
			{": "}
			{value}
		</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={
				<HeaderValue variant="renal-replacement-therapy">
					{content}
				</HeaderValue>
			}
		/>
	);
};

const Dm: React.FunctionComponent = () => {
	const users = useKnownUsers();
	const { value, timestamp, origin } = useDm();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>
			<FormattedMessage {...messages.dm} />
			{": "}
			{value}
		</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="dm">{content}</HeaderValue>}
		/>
	);
};

const LiveDonorScreening: React.FunctionComponent = () => {
	const users = useKnownUsers();
	const { value, timestamp, origin } = useLiveDonorScreening();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>
			<FormattedMessage {...messages.liveDonorScreening} />
			{": "}
			{value}
		</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="l_d_s">{content}</HeaderValue>}
		/>
	);
};

const Ht: React.FunctionComponent = () => {
	const users = useKnownUsers();
	const { value, timestamp, origin } = useHt();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>
			<FormattedMessage {...messages.ht} />
			{": "}
			{value}
		</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="ht">{content}</HeaderValue>}
		/>
	);
};

const NephrologistOpinion: React.FunctionComponent = () => {
	const users = useKnownUsers();
	const { value, timestamp, origin } = useNephrologistOpinion();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>
			<FormattedMessage {...messages.nephrologistOpinion} />
			{": "}
			{value}
		</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="n_o">{content}</HeaderValue>}
		/>
	);
};

const WaitingListTime: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin, days } = useWaitingListTime();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content;

	const message = days
		? waitingListMessages.waitingListDays
		: waitingListMessages.waitingListMonths;
	if (value !== undefined) {
		content = <FormattedMessage {...message} values={{ n: value }} />;
	}

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={
				<HeaderValue variant="waiting-list-time">{content}</HeaderValue>
			}
		/>
	);
};

const PreviousOtherOrganTransplantation: React.FunctionComponent = () => {
	const value = usePreviousOtherOrganTransplantation();
	return (
		<HeaderValue variant="prev_trans">
			<FormattedMessage {...messages.prevTrans} />
			{": "}
			{value}
		</HeaderValue>
	);
};
